/**
 * Detect "based on week N" / "week N plan" / "第 N 周" intent phrases
 * in chat composer text. Conservative on purpose — only fires when
 * the user's phrasing makes the plan reference unambiguous. A bare
 * "we had a tough week 3" deliberately produces nothing.
 *
 * Returns deduplicated week numbers in stable ascending order so the
 * caller can iterate predictably (multi-week mentions are valid:
 * "compare week 3 and week 5 plans").
 */

// English patterns. The second group captures the week number.
const EN_INTENT = [
  // "based on week 3", "for week 3", "about week 3", "from week 3", "using week 3"
  /\b(?:based on|for|about|from|using|reference|referencing)\s+week\s+(\d{1,2})\b/gi,
  // "week 3 plan", "week 3 ppt", "week 3 deck", "week 3 slides", "week 3 presentation"
  /\bweek\s+(\d{1,2})\s+(?:plan|ppt|pptx|deck|slides?|presentation|materials?|lesson)\b/gi,
  // "ppt about week 3", "presentation on week 3"
  /\b(?:ppt|pptx|deck|presentation|slides?)\s+(?:about|on|for|based on|from)\s+week\s+(\d{1,2})\b/gi,
];

// Chinese patterns. Group 1 is the week number; tolerate the
// "第 N 周" / "周 N" / "N 周" spellings and the verb cues.
const ZH_INTENT = [
  // "第 3 周的", "第3周的教学计划", "基于第 3 周"
  /(?:基于|按照|关于|根据|参考|针对)\s*第?\s*(\d{1,2})\s*周/g,
  // "第 3 周的 ppt / 计划 / 课件"
  /第\s*(\d{1,2})\s*周\s*的?\s*(?:ppt|pptx|课件|计划|教案|演示|讲稿)/gi,
];

const ALL_PATTERNS = [...EN_INTENT, ...ZH_INTENT];

export function detectMentionedWeeks(text: string): number[] {
  if (!text || text.length === 0) return [];
  const found = new Set<number>();
  for (const re of ALL_PATTERNS) {
    // Patterns above use the `g` flag — reset lastIndex so re-running
    // the same regex across renders doesn't skip matches.
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const n = Number.parseInt(m[1] ?? "", 10);
      if (Number.isFinite(n) && n > 0 && n <= 52) {
        found.add(n);
      }
    }
  }
  return [...found].sort((a, b) => a - b);
}
